import { Fragment, useMemo } from 'react';

import { useIntl } from 'react-intl';

import UFOSegment from '@atlaskit/react-ufo/segment';
import { Editions, ProductKeys } from '@atlassiansox/asc-stargate';

import { useCrossFlowFrontendTouchpoint } from '@/components/cross-flow/touchpoint/use-touchpoint/index';
import { ConfluenceSmartBanner } from '@/components/smart-banner/src/confluence-smart-banner';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { LogoV3 } from '@/libraries/wpl/foundations/src/logo-v3';
import { DropdownContent } from '@/patterns/global-nav/src/dropdown-content';
import { GlobalNavLinkContentProps } from '@/patterns/global-nav/src/nav-links-container';
import { ProductPageSubNav } from '@/patterns/global-nav/src/nav-product-page';

const ConfluenceLogoComponent = () => {
	return <LogoV3 brand="confluence" type="logo" appearance="brand" />;
};

const ConfluenceSoftwareNav = () => {
	const intl = useIntl();

	const signupLink = useLocalizedHref('/try/cloud/signup?bundle=confluence&edition=free');
	const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
		productKey: ProductKeys.CONFLUENCE,
		edition: Editions.FREE,
		redirectUrl: signupLink,
	});

	// Template dropdown hrefs
	const allTemplatesHref = useLocalizedHref('/software/confluence/templates');
	const designHref = useLocalizedHref('/software/confluence/templates/categories/design');
	const financeAndOpsHref = useLocalizedHref(
		'/software/confluence/templates/categories/finance-and-operations',
	);
	const humanResourcesHref = useLocalizedHref(
		'/software/confluence/templates/categories/human-resources',
	);
	const marketingAndSalesHref = useLocalizedHref(
		'/software/confluence/templates/categories/marketing-and-sales',
	);
	const productManagementHref = useLocalizedHref(
		'/software/confluence/templates/categories/product-management',
	);
	const projectManagementHref = useLocalizedHref(
		'/software/confluence/templates/categories/project-management',
	);
	const recommendedHref = useLocalizedHref('/software/confluence/templates/categories/recommended');
	const softwareAndItHref = useLocalizedHref(
		'/software/confluence/templates/categories/software-development-and-it',
	);

	// Features dropdown hrefs
	const allFeaturesHref = useLocalizedHref('/software/confluence/features');
	const rovoInConfluenceHref = useLocalizedHref('/software/confluence/ai');

	const templatesDropdown = useMemo(
		() => (
			<DropdownContent
				contentSections={[
					{
						links: [
							{
								text: intl.formatMessage({
									id: 'confluence.TemplateCategoriesDropdown.allTemplates',
									defaultMessage: 'All templates',
								}),
								href: allTemplatesHref,
							},
							{
								text: intl.formatMessage({
									id: 'confluence.TemplateCategoriesDropdown.design',
									defaultMessage: 'Design',
								}),
								href: designHref,
							},
							{
								text: intl.formatMessage({
									id: 'confluence.TemplateCategoriesDropdown.financeAndOperations',
									defaultMessage: 'Finance & Ops',
								}),
								href: financeAndOpsHref,
							},
							{
								text: intl.formatMessage({
									id: 'confluence.TemplateCategoriesDropdown.humanResources',
									defaultMessage: 'Human Resources',
								}),
								href: humanResourcesHref,
							},
							{
								text: intl.formatMessage({
									id: 'confluence.TemplateCategoriesDropdown.marketingAndSales',
									defaultMessage: 'Marketing & Sales',
								}),
								href: marketingAndSalesHref,
							},
							{
								text: intl.formatMessage({
									id: 'confluence.TemplateCategoriesDropdown.productManagement',
									defaultMessage: 'Product Management',
								}),
								href: productManagementHref,
							},
							{
								text: intl.formatMessage({
									id: 'confluence.TemplateCategoriesDropdown.projectManagement',
									defaultMessage: 'Project Management',
								}),
								href: projectManagementHref,
							},
							{
								text: intl.formatMessage({
									id: 'confluence.TemplateCategoriesDropdown.recommended',
									defaultMessage: 'Recommended',
								}),
								href: recommendedHref,
							},
							{
								text: intl.formatMessage({
									id: 'confluence.TemplateCategoriesDropdown.softwareDevelopmentAndIt',
									defaultMessage: 'Software & IT',
								}),
								href: softwareAndItHref,
							},
						],
					},
				]}
			/>
		),
		[
			intl,
			allTemplatesHref,
			designHref,
			financeAndOpsHref,
			humanResourcesHref,
			marketingAndSalesHref,
			productManagementHref,
			projectManagementHref,
			recommendedHref,
			softwareAndItHref,
		],
	);
	const featuresDropdown = useMemo(
		() => (
			<DropdownContent
				contentSections={[
					{
						links: [
							{
								text: intl.formatMessage({
									id: 'ConfluenceSoftwarePageTemplate.navbar.features.allFeatures',
									defaultMessage: 'All Features',
								}),
								href: allFeaturesHref,
							},
							{
								text: intl.formatMessage({
									id: 'ConfluenceSoftwarePageTemplate.navbar.features.rovoInConfluence',
									defaultMessage: 'Rovo in Confluence',
								}),
								href: rovoInConfluenceHref,
							},
						],
					},
				]}
			/>
		),
		[intl, allFeaturesHref, rovoInConfluenceHref],
	);

	const templatesLinkContent = useMemo<GlobalNavLinkContentProps>(() => {
		const common = {
			globalLinksKey: 'templates',
			linkText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.navbar.templates',
				defaultMessage: 'Templates',
			}),
			productKey: 'confluence',
		};

		return {
			...common,
			hasSubnav: true,
			// TODO: replace with TemplateCategoriesDropdown to have dynamic categories
			content: templatesDropdown,
			dropdownType: 'templates',
			containerPosition: 'relative',
		};
	}, [intl, templatesDropdown]);

	const ctaText = intl.formatMessage({
		id: 'ConfluenceSoftwarePageTemplate.navbar.getItFree',
		defaultMessage: 'Get it free',
	});

	const links: GlobalNavLinkContentProps[] = [
		{
			globalLinksKey: 'features',
			linkText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.navbar.features',
				defaultMessage: 'Features',
			}),
			hasSubnav: true,
			content: featuresDropdown,
			productKey: 'confluence',
			containerPosition: 'relative',
		},
		{
			globalLinksKey: 'resources',
			linkText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.navbar.resources',
				defaultMessage: 'Resources',
			}),
			hasSubnav: false,
			productKey: 'confluence',
			href: useLocalizedHref('/software/confluence/resources'),
		},
		{
			globalLinksKey: 'guides',
			linkText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.navbar.guides',
				defaultMessage: 'Guides',
			}),
			hasSubnav: false,
			productKey: 'confluence',
			href: useLocalizedHref(
				'/software/confluence/resources/guides/choosing-confluence/team-on-same-page',
			),
		},
		templatesLinkContent,
		{
			globalLinksKey: 'enterprise',
			linkText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.navbar.enterprise',
				defaultMessage: 'Enterprise',
			}),
			hasSubnav: false,
			productKey: 'confluence',
			href: useLocalizedHref('/software/confluence/enterprise'),
		},
		{
			globalLinksKey: 'pricing',
			linkText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.navbar.pricing',
				defaultMessage: 'Pricing',
			}),
			hasSubnav: false,
			productKey: 'confluence',
			href: useLocalizedHref('/software/confluence/pricing'),
		},
	];

	return (
		<Fragment>
			<ConfluenceSmartBanner />
			<UFOSegment name="confluence-pt-cfind-1234-confluenceSoftwareNav">
				<ProductPageSubNav
					productLogoUrl={useLocalizedHref('/software/confluence')}
					productLogo={<ConfluenceLogoComponent />}
					productLogoAriaLabel={intl.formatMessage({
						id: 'ConfluenceSoftwarePageTemplate.navbar.confluence-logo',
						defaultMessage: 'Confluence logo',
					})}
					mobileCtaText={intl.formatMessage({
						id: 'ConfluenceSoftwarePageTemplate.navbar.getitfree',
						defaultMessage: 'Get it free',
					})}
					navigationTrynowCtaText={ctaText}
					mobileCtaUrl={signupLink}
					mobileCtaOnClick={openCrossFlow}
					mobileLogo={<ConfluenceLogoComponent />}
					productPageNavLinks={links}
					navigationTrynowCtaUrl={signupLink}
					navigationTrynowCtaOnClick={openCrossFlow}
					mobileLinksSections={[
						{
							mobileLinkKey: 'mobile-confluence',
							mobileLinksProps: links,
						},
					]}
					additionalMenuPageObjectsArray={[
						{
							menuPageKey: 'templates',
							menuPageObjects: templatesLinkContent?.content as JSX.Element,
						},
					]}
				/>
			</UFOSegment>
		</Fragment>
	);
};

ConfluenceSoftwareNav.displayName = 'ConfluenceSoftwareNav';

export default ConfluenceSoftwareNav;
